// The published product template owns the opt-in. This module never cancels touch,
// pointer or wheel gestures: native page pinch zoom stays available throughout.
const PUBLISHED_HOSTS = ['raffi-jewellers.ca', 'www.raffi-jewellers.ca'];
const PROTECTED_PRODUCT = /rolex|tag(?:[\s_+\-]|%20)*heuer|inquiry-only/i;
const IMAGE_BUTTON = '.single-product__gallery-btn[data-lightbox-src]';
let dispose: (() => void) | null = null;
let closeActive: (() => void) | null = null;
let dialogNumber = 0;

type ProductImage = { src: string; alt: string; button: HTMLButtonElement };

export function productImageLightboxEnabled(main: HTMLElement, location: Pick<Location, 'hostname' | 'pathname' | 'search'> = window.location): boolean {
  const product = main.querySelector<HTMLElement>('.js-product');
  const language = new URLSearchParams(location.search).get('lang');
  return main.hasAttribute('data-product-image-lightbox')
    && PUBLISHED_HOSTS.includes(location.hostname)
    && /^\/(?:fr\/)?products\/[a-z0-9_-]+\/?$/i.test(location.pathname)
    && (!language || /^(?:en|fr)$/.test(language))
    && !/[?&](?:key|inquiry)=/i.test(location.search)
    && !PROTECTED_PRODUCT.test([location.pathname, product?.dataset.brand || '', product?.dataset.product || ''].join(' '));
}

const photoSource = (raw: string | undefined): string | null => {
  if (!raw || /placeholder/i.test(raw)) return null;
  try {
    const url = new URL(raw, window.location.href);
    return /^https?:$/.test(url.protocol) ? url.href : null;
  } catch { return null; }
};

const icon = (path: string): string => `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="${path}"/></svg>`;

export function initProductImageLightbox(): void {
  dispose?.(); dispose = null;
  closeActive?.(); closeActive = null;
  const main = document.querySelector<HTMLElement>('main[data-product-image-lightbox]');
  if (!main || !productImageLightboxEnabled(main) || typeof HTMLDialogElement === 'undefined') return;
  const language = new URLSearchParams(window.location.search).get('lang');
  const french = language === 'fr' || /^\/fr\//.test(window.location.pathname) || (!language && document.documentElement.lang.startsWith('fr'));
  const copy = french ? {
    title: 'Détails du produit', close: 'Fermer les images', loading: 'Chargement de l’image…',
    failed: 'Cette image ne peut pas être chargée. Essayez une autre vue.',
    previous: 'Image précédente', next: 'Image suivante', zoomIn: 'Agrandir', zoomOut: 'Réduire',
    reset: 'Réinitialiser le zoom', imageArea: 'Image du produit agrandie',
    hint: 'Pincez ou touchez pour agrandir. Faites défiler pour explorer.',
    wrist: 'Découvrez-la à votre poignet', tryOn: 'Essayer la montre', of: 'sur', reference: 'Réf.',
  } : {
    title: 'Product details', close: 'Close images', loading: 'Loading image…',
    failed: 'This image could not load. Try another view.',
    previous: 'Previous image', next: 'Next image', zoomIn: 'Zoom in', zoomOut: 'Zoom out',
    reset: 'Reset zoom', imageArea: 'Enlarged product image',
    hint: 'Pinch or tap to zoom. Scroll to explore.',
    wrist: 'See it on your wrist', tryOn: 'Try it on', of: 'of', reference: 'Ref.',
  };

  // Observe touches passively only to ignore the synthetic click after a pinch
  // or scroll. Never cancel a native browser gesture, including over the image.
  let ignoreClicksUntil = 0;
  let touchOrigin: { x: number; y: number } | null = null;
  const touchStart = (event: TouchEvent) => {
    if (event.touches.length > 1) {
      ignoreClicksUntil = Date.now() + 800;
      touchOrigin = null;
    } else if (event.touches.length === 1) {
      touchOrigin = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
  };
  const touchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    if (event.touches.length > 1 || (touch && touchOrigin && Math.hypot(touch.clientX - touchOrigin.x, touch.clientY - touchOrigin.y) > 8)) {
      ignoreClicksUntil = Date.now() + 800;
    }
  };
  const touchEnd = () => { touchOrigin = null; };
  main.addEventListener('touchstart', touchStart, { passive: true });
  main.addEventListener('touchmove', touchMove, { passive: true });
  main.addEventListener('touchend', touchEnd, { passive: true });
  main.addEventListener('touchcancel', touchEnd, { passive: true });
  const originalTabStops = new Map<HTMLButtonElement, string | null>();
  const syncGalleryTabStops = () => {
    main.querySelectorAll<HTMLButtonElement>(IMAGE_BUTTON).forEach((button) => {
      if (!originalTabStops.has(button)) originalTabStops.set(button, button.getAttribute('tabindex'));
      const slide = button.closest('.js-slide');
      button.tabIndex = !slide || slide.classList.contains('is-selected') ? 0 : -1;
    });
  };
  syncGalleryTabStops();
  document.addEventListener('product:image-change', syncGalleryTabStops);

  const open = (trigger: HTMLElement, initialButton: HTMLButtonElement | null) => {
    if (Date.now() < ignoreClicksUntil || !productImageLightboxEnabled(main)) return;
    const images: ProductImage[] = [];
    main.querySelectorAll<HTMLButtonElement>(IMAGE_BUTTON).forEach((button) => {
      const image = button.querySelector('img');
      const src = photoSource(button.dataset.lightboxSrc);
      if (!image || !src || button.closest('.js-slide')?.querySelector('video')) return;
      images.push({ src, alt: button.dataset.lightboxAlt || image.alt || copy.title, button });
    });
    if (!images.length) return;
    closeActive?.();
    let index = Math.max(0, images.findIndex((image) => image.button === initialButton));
    let zoom = 1;
    let loaded = false;
    let request = 0;
    let restoreFocus = true;
    const dialog = document.createElement('dialog');
    const titleId = `rf-product-image-title-${++dialogNumber}`;
    const hintId = `rf-product-image-hint-${dialogNumber}`;
    dialog.className = 'rf-product-image-dialog';
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.innerHTML = `
      <header class="rf-product-image-dialog__header">
        <div class="rf-product-image-dialog__heading"><h2 id="${titleId}"></h2><p class="rf-product-image-dialog__reference"></p></div>
        <button type="button" class="rf-product-image-dialog__icon" data-image-close aria-label="${copy.close}" autofocus>${icon('M6 6l12 12M18 6 6 18')}</button>
      </header>
      <div class="rf-product-image-dialog__stage" role="region" aria-label="${copy.imageArea}" aria-describedby="${hintId}" tabindex="0" aria-busy="true">
        <div class="rf-product-image-dialog__message" role="status" aria-live="polite"></div>
        <div class="rf-product-image-dialog__canvas" hidden><img class="rf-product-image-dialog__photo" alt="" draggable="false"></div>
      </div>
      <div class="rf-product-image-dialog__toolbar">
        <div class="rf-product-image-dialog__navigation">
          <button type="button" class="rf-product-image-dialog__icon" data-image-previous aria-label="${copy.previous}">${icon('M14 6l-6 6 6 6')}</button>
          <span class="rf-product-image-dialog__count" aria-live="polite"></span>
          <button type="button" class="rf-product-image-dialog__icon" data-image-next aria-label="${copy.next}">${icon('M10 6l6 6-6 6')}</button>
        </div>
        <div class="rf-product-image-dialog__zoom">
          <button type="button" class="rf-product-image-dialog__icon" data-image-out aria-label="${copy.zoomOut}">${icon('M5 12h14')}</button>
          <output class="rf-product-image-dialog__percent" aria-live="polite">100%</output>
          <button type="button" class="rf-product-image-dialog__icon" data-image-in aria-label="${copy.zoomIn}">${icon('M5 12h14M12 5v14')}</button>
          <button type="button" class="rf-product-image-dialog__icon" data-image-reset aria-label="${copy.reset}">${icon('M5 8V3M5 8h5M5 8a8 8 0 1 1-1 7')}</button>
        </div>
      </div>
      <p class="rf-product-image-dialog__hint" id="${hintId}">${copy.hint}</p>
      <div class="rf-product-image-dialog__tryon" hidden><span>${copy.wrist}</span><button type="button" data-image-tryon>${copy.tryOn}<span aria-hidden="true">↗</span></button></div>`;
    const stage = dialog.querySelector<HTMLElement>('.rf-product-image-dialog__stage')!;
    const canvas = dialog.querySelector<HTMLElement>('.rf-product-image-dialog__canvas')!;
    const photo = dialog.querySelector<HTMLImageElement>('.rf-product-image-dialog__photo')!;
    const message = dialog.querySelector<HTMLElement>('.rf-product-image-dialog__message')!;
    const zoomIn = dialog.querySelector<HTMLButtonElement>('[data-image-in]')!;
    const zoomOut = dialog.querySelector<HTMLButtonElement>('[data-image-out]')!;
    const reset = dialog.querySelector<HTMLButtonElement>('[data-image-reset]')!;
    const percent = dialog.querySelector<HTMLOutputElement>('.rf-product-image-dialog__percent')!;
    const heading = main.querySelector<HTMLElement>('.single-product__title')?.textContent?.trim() || copy.title;
    const reference = main.querySelector<HTMLElement>('.single-product__sku')?.textContent?.trim();
    dialog.querySelector('h2')!.textContent = heading;
    const referenceLine = dialog.querySelector<HTMLElement>('.rf-product-image-dialog__reference')!;
    referenceLine.textContent = reference ? `${copy.reference} ${reference}` : '';
    referenceLine.hidden = !reference;
    dialog.querySelector<HTMLElement>('.rf-product-image-dialog__navigation')!.hidden = images.length < 2;

    const getTryOn = () => main.querySelector<HTMLButtonElement>('.single-product__details [data-watch-tryon]:not([disabled])');
    dialog.querySelector<HTMLElement>('.rf-product-image-dialog__tryon')!.hidden = !getTryOn();

    const updateControls = () => {
      zoomIn.disabled = !loaded || zoom >= 3;
      zoomOut.disabled = !loaded || zoom <= 1;
      reset.disabled = !loaded || zoom === 1;
      percent.value = `${Math.round(zoom * 100)}%`;
      photo.dataset.zoomed = String(zoom > 1);
    };
    const fitImage = (preserveCenter = true) => {
      if (!loaded || !photo.naturalWidth || !stage.clientWidth || !stage.clientHeight) return;
      const centerX = preserveCenter && canvas.scrollWidth ? (stage.scrollLeft + stage.clientWidth / 2) / canvas.scrollWidth : 0.5;
      const centerY = preserveCenter && canvas.scrollHeight ? (stage.scrollTop + stage.clientHeight / 2) / canvas.scrollHeight : 0.5;
      const ratio = Math.min(Math.max(1, stage.clientWidth - 32) / photo.naturalWidth, Math.max(1, stage.clientHeight - 32) / photo.naturalHeight);
      const width = photo.naturalWidth * ratio * zoom;
      const height = photo.naturalHeight * ratio * zoom;
      photo.style.width = `${width}px`;
      photo.style.height = `${height}px`;
      canvas.style.width = `${Math.max(stage.clientWidth, width + 32)}px`;
      canvas.style.height = `${Math.max(stage.clientHeight, height + 32)}px`;
      stage.scrollLeft = canvas.scrollWidth * centerX - stage.clientWidth / 2;
      stage.scrollTop = canvas.scrollHeight * centerY - stage.clientHeight / 2;
    };
    const setZoom = (value: number) => {
      if (!loaded) return;
      zoom = Math.max(1, Math.min(3, value));
      fitImage();
      updateControls();
    };
    const loadImage = () => {
      const current = ++request;
      loaded = false; zoom = 1;
      canvas.hidden = true;
      message.hidden = false;
      message.textContent = copy.loading;
      stage.setAttribute('aria-busy', 'true');
      photo.alt = images[index].alt;
      dialog.querySelector('.rf-product-image-dialog__count')!.textContent = `${index + 1} ${copy.of} ${images.length}`;
      updateControls();
      photo.onload = () => {
        if (current !== request || !dialog.open) return;
        loaded = true;
        message.hidden = true;
        canvas.hidden = false;
        stage.setAttribute('aria-busy', 'false');
        fitImage(false);
        updateControls();
      };
      photo.onerror = () => {
        if (current !== request || !dialog.open) return;
        canvas.hidden = true;
        message.textContent = copy.failed;
        stage.setAttribute('aria-busy', 'false');
        updateControls();
      };
      photo.src = images[index].src;
    };
    const move = (direction: number) => {
      if (images.length < 2) return;
      index = (index + direction + images.length) % images.length;
      loadImage();
    };
    const close = () => { if (dialog.open) dialog.close(); };
    closeActive = close;
    dialog.querySelector('[data-image-close]')!.addEventListener('click', close);
    dialog.querySelector('[data-image-previous]')!.addEventListener('click', () => move(-1));
    dialog.querySelector('[data-image-next]')!.addEventListener('click', () => move(1));
    zoomIn.addEventListener('click', () => setZoom(zoom + 0.5));
    zoomOut.addEventListener('click', () => setZoom(zoom - 0.5));
    reset.addEventListener('click', () => setZoom(1));
    photo.addEventListener('click', (event) => {
      if (Date.now() < ignoreClicksUntil || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      setZoom(zoom === 1 ? 2 : 1);
    });
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog || Date.now() < ignoreClicksUntil) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
    });
    dialog.addEventListener('keydown', (event) => {
      // Browser zoom shortcuts (Ctrl/Cmd +/-) must continue to work normally.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
      else if (event.key === '+' || event.key === '=') { event.preventDefault(); setZoom(zoom + 0.5); }
      else if (event.key === '-') { event.preventDefault(); setZoom(zoom - 0.5); }
      else if (event.key === '0') { event.preventDefault(); setZoom(1); }
    });
    dialog.querySelector('[data-image-tryon]')!.addEventListener('click', () => {
      const button = getTryOn();
      if (!button) return;
      restoreFocus = false;
      close();
      // Use the existing entry point and its consent/tracking path. A native
      // dialog must be closed before the try-on experience receives focus.
      button.focus({ preventScroll: true });
      button.click();
    });
    const resize = new ResizeObserver(() => fitImage());
    dialog.addEventListener('close', () => {
      request++;
      resize.disconnect();
      photo.onload = null; photo.onerror = null;
      dialog.remove();
      if (closeActive === close) closeActive = null;
      if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    }, { once: true });
    main.appendChild(dialog);
    dialog.showModal();
    resize.observe(stage);
    loadImage();
  };

  const onClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element) || event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.button !== 0) return;
    const trigger = event.target.closest<HTMLElement>(`${IMAGE_BUTTON}, [data-product-image-open]`);
    if (!trigger || !main.contains(trigger)) return;
    const slide = trigger.matches(IMAGE_BUTTON) ? trigger.closest('.js-slide') : null;
    if (slide && !slide.classList.contains('is-selected')) return;
    const selected = trigger.matches(IMAGE_BUTTON) ? trigger as HTMLButtonElement
      : main.querySelector<HTMLButtonElement>(`.js-slide.is-selected ${IMAGE_BUTTON}`)
        || main.querySelector<HTMLButtonElement>(IMAGE_BUTTON);
    open(trigger, selected);
  };
  main.addEventListener('click', onClick);
  dispose = () => {
    main.removeEventListener('click', onClick);
    main.removeEventListener('touchstart', touchStart);
    main.removeEventListener('touchmove', touchMove);
    main.removeEventListener('touchend', touchEnd);
    main.removeEventListener('touchcancel', touchEnd);
    document.removeEventListener('product:image-change', syncGalleryTabStops);
    originalTabStops.forEach((value, button) => {
      if (value === null) button.removeAttribute('tabindex');
      else button.setAttribute('tabindex', value);
    });
  };
}

document.addEventListener('DOMContentLoaded', initProductImageLightbox);
document.addEventListener('product:swap', initProductImageLightbox);
window.addEventListener('pagehide', () => { closeActive?.(); dispose?.(); dispose = null; });
window.addEventListener('pageshow', (event) => { if (event.persisted) initProductImageLightbox(); });
